import type { Level, LevelRecord, Phase, Point } from "./types";
import type { PrizeRoutingRequest } from "@jpjj/solver-core/prize";
import { LEVELS } from "./levels";

/** Build the solver request for a level (same instance the player faces). */
export function requestFor(level: Level): PrizeRoutingRequest {
  return {
    points: level.points,
    loot: level.loot,
    capacity: level.capacity,
    budget: level.budget,
  };
}

/** Mutable, session-global game state, kept in one object so feature modules
 *  share it without a tangle of cross-module setters. */
export const state = {
  phase: "intro" as Phase,
  /** Current room index. */
  idx: 0,

  /** The hero's current cell. */
  hero: [0, 0] as Point,
  /** Loot node indices carried right now (picked up, not yet in the cart). */
  carried: [] as number[],
  /** Node indices already banked in the cart this room. */
  banked: [] as number[],
  /** Completed (banked) trips this room, each a node sequence [0, …, 0]. */
  trips: [] as number[][],
  /** Total steps walked this room. */
  steps: 0,

  /** Best banked result per room, fed to scoring + the reveal. */
  records: LEVELS.map((): LevelRecord => ({ prize: 0, trips: [], steps: 0, played: false })),

  level(): Level { return LEVELS[this.idx]; },
  /** Total weight currently carried. */
  load(): number {
    const L = this.level();
    return this.carried.reduce((s, k) => s + L.loot[k - 1].weight, 0);
  },
  /** Prize currently carried (at risk until banked). */
  carriedPrize(): number {
    const L = this.level();
    return this.carried.reduce((s, k) => s + L.loot[k - 1].prize, 0);
  },
  /** Prize banked in the cart this room. */
  bankedPrize(): number {
    const L = this.level();
    return this.banked.reduce((s, k) => s + L.loot[k - 1].prize, 0);
  },
  /** Steps remaining before the monster arrives. */
  stepsLeft(): number { return this.level().budget - this.steps; },
  /** Is the hero standing on the cart? */
  atCart(): boolean {
    const c = this.level().points[0];
    return this.hero[0] === c[0] && this.hero[1] === c[1];
  },
};

/** Reset the live interaction state for entering a room fresh. */
export function enterRoom(idx: number): void {
  state.idx = idx;
  const L = LEVELS[idx];
  state.hero = [...L.points[0]] as Point;
  state.carried = [];
  state.banked = [];
  state.trips = [];
  state.steps = 0;
}
